import { SecuredLayout } from '../Layout/SecuredLayout';
import Grid from '@mui/material/Unstable_Grid2';
import BusCards from './BusCards';
import {
    Box,
    Breadcrumbs,
    Button,
    Fab,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import PriceOffers from './PriceOffers';
import { useEffect, useState } from 'react';
import { StyledDetailsBox } from '../Layout/styles';
import ServiceAreaMap from './ServiceAreaMap';
import { Bus, Contractor, PhoneNumber, QRCodeListItem, Quotation, ServiceLevel, Shire } from '../../@types/contractors';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useAuth, useProvideSnackBar } from '../../hooks';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import CreateOrShowKeycloakUser from './CreateOrShowKeycloakUser';
import { usePreconfiguredFetch } from '../../hooks/useProvidePreconfiguredFetch';
import {
    Delete,
    DescriptionOutlined,
    DirectionsBusFilledOutlined,
    LocalOfferOutlined,
    QrCode2Outlined
} from '@mui/icons-material';
import ConfirmDialog from '../Common/ConfirmDialog';
import { Role } from '../../@types/auth';
import { QRCodeTab } from './QRCodeTab';

enum ContractorTab {
    CORE_DATA = 0,
    BUSES = 1,
    PRICE_OFFERS = 2,
    QR_CODES = 3,
}

export default function DetailsPage() {
    const { fetchResource } = usePreconfiguredFetch();
    const { uuid } = useParams<{ uuid: string }>();
    const navigate = useNavigate();
    const { showError, showSuccess, showResponseError } = useProvideSnackBar();
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [site, setSite] = useState<string>('');
    const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([{ Value: '' }]);
    const [serviceLevel, setServiceLevel] = useState<ServiceLevel | ''>('please_select' as ServiceLevel);
    const [keycloakUserId, setKeycloakUserId] = useState<string | undefined>();
    const [keycloakUsername, setKeycloakUsername] = useState<string | undefined>();
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedShires, setSelectedShires] = useState<Shire[]>([]);
    const [quotation, setQuotation] = useState<Quotation[]>([]);
    const [busForm, setBusForm] = useState<Bus[]>([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [qrCodes, setQrCodes] = useState<QRCodeListItem[] | null>(null);

    const [originalState, setOriginalState] = useState<Contractor>({
        ID: 0,
        Uuid: '',
        Name: '',
        Email: '',
        Site: '',
        PhoneNumbers: [],
        Shires: [],
        ServiceLevel: 'please_select' as ServiceLevel,
        Buses: [],
        Quotation: [],
        Subscribed: true,
        KeycloakUuid: undefined,
        KeycloakUsername: undefined,
    });
    const { user } = useAuth();
    const isBrAdmin = !!user?.roles?.includes(Role.ADMIN);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);
    };

    const fetchCreateBus = async (contractorID: string) => {
        try {
            const response = await fetchResource(`/api/v1/contractor/${contractorID}/bus/create`, {
                method: 'POST',
                body: JSON.stringify(busForm),
            });
            if (response.ok) {
                const data = await response.json();
                return data.ID;
            } else {
                showResponseError(response);
            }
        } catch (error: any) {
            showError(`Hiba történt az adat elküldése közben`);
        }
    };

    const handleCreateBus = async (contractorID: string) => {
        if (contractorID === '') {
            showError('Kérjük, töltse ki a törzsadatokat majd kattintson a mentés gombra!');
            return;
        }
        const busID = await fetchCreateBus(contractorID);

        if (busID) {
            setBusForm(prev => [
                ...prev,
                {
                    ID: busID,
                    Manufacturer: '',
                    Type: '',
                    YearOfManufacture: '',
                    Capacity: 49,
                    Category: 'nagy',
                },
            ]);
        }
    };

    const fetchCreateContractor = async () => {
        try {
            const response = await fetchResource('/api/v1/contractor', {
                method: 'POST',
                body: JSON.stringify({
                    Name: name,
                    Email: email,
                    Site: site,
                    PhoneNumbers: phoneNumbers,
                    Shires: selectedShires,
                    ServiceLevel: serviceLevel,
                }),
            });
            if (response.ok) {
                showSuccess('Sikeres mentés!');
                navigate('/');
            } else {
                showResponseError(response);
            }
        } catch (error: any) {
            showError(`Hiba történt az adat elküldése közben`);
        }
    };

    const handleShiresClick = (shireName: string) => {
        if (selectedShires.some(shire => shire.Name === shireName)) {
            setSelectedShires(prevShires => prevShires.filter(shire => shire.Name !== shireName));
            return;
        }
        setSelectedShires(prevShires => [...prevShires, { Name: shireName }]);
    };

    const handleAddPhoneNumber = () => setPhoneNumbers(prevPhoneNumbers => [...prevPhoneNumbers, { Value: '' }]);

    const handleDeletePhoneNumber = (index: number) => {
        const newPhoneNumbers = phoneNumbers.filter((_, i) => i !== index);
        setPhoneNumbers(newPhoneNumbers);
    };

    const fetchGetContractor = async (uuid: string) => {
        try {
            const response = await fetchResource(`/api/v1/contractor/${uuid}`);

            if (response.ok) {
                const existingContractor: Contractor = await response.json();
                const { Quotation } = existingContractor;
                Quotation.sort((q1, q2) => new Date(q2.CreatedAt).getTime() - new Date(q1.CreatedAt).getTime());
                setName(existingContractor.Name);
                setEmail(existingContractor.Email);
                setSite(existingContractor.Site);
                setPhoneNumbers(
                    existingContractor.PhoneNumbers.map(phoneNumber => ({
                        ID: phoneNumber.ID,
                        Value: phoneNumber.Value,
                    }))
                );
                setServiceLevel(existingContractor.ServiceLevel);
                setSelectedShires(existingContractor.Shires);
                setBusForm(existingContractor.Buses);
                setQuotation(Quotation);
                setOriginalState(existingContractor);
                setKeycloakUserId(existingContractor.KeycloakUuid);
                setKeycloakUsername(existingContractor.KeycloakUsername);
            } else {
                showResponseError(response);
            }
        } catch (error: any) {
            showError(`Hiba történt az adat lekérdezése közben`);
        }
    };

    useEffect(() => {
        if (uuid) {
            fetchGetContractor(uuid);
        }
    }, [uuid]);

    const fetchUpdateContractor = async (uuid: string) => {
        const contractorToUpdate = {
            Name: name,
            Email: email,
            Site: site,
            ServiceLevel: serviceLevel,
            PhoneNumbers: phoneNumbers,
            Shires: selectedShires,
            Buses: busForm,
        };
        try {
            const response = await fetchResource(`/api/v1/contractor/${uuid}`, {
                method: 'PUT',
                body: JSON.stringify(contractorToUpdate),
            });

            if (response.ok) {
                showSuccess('Az adatok sikeresen frissítve lettek!');
                fetchGetContractor(uuid);
            } else {
                showResponseError(response);
            }
        } catch (error: any) {
            showError(`Hiba történt az adat frissítése közben`);
        }
    };

    const handleSaveContractor = () => {
        if (uuid) {
            fetchUpdateContractor(uuid);
            return;
        }
        fetchCreateContractor();
    };

    const hasChanges = () => {
        const data = {
            Name: name,
            Email: email,
            Site: site,
            ServiceLevel: serviceLevel,
            PhoneNumbers: phoneNumbers.map(phoneNumber => phoneNumber.Value),
            Shires: selectedShires,
            Buses: busForm,
        };
        return (
            JSON.stringify({
                Name: originalState.Name,
                Email: originalState.Email,
                Site: originalState.Site,
                ServiceLevel: originalState.ServiceLevel,
                PhoneNumbers: originalState.PhoneNumbers.map(phoneNumber => phoneNumber.Value),
                Shires: originalState.Shires,
                Buses: originalState.Buses,
            }) !== JSON.stringify(data)
        );
    };

    const handleConfirmDeleteContractor = async () => {
        if (!uuid) {
            return;
        }

        try {
            const response = await fetchResource(`/api/v1/contractor/${uuid}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                showSuccess('Sikeres törlés!');
                navigate('/');
            } else {
                showResponseError(response);
            }
        } catch (error: any) {
            showError('Hiba történt a törlés közben');
        } finally {
            setOpenDeleteDialog(false);
        }
    };

    const renderTabContent = () => {
        switch (selectedTab) {
            case ContractorTab.CORE_DATA:
                return (
                    <Grid
                        container
                        sx={{
                            width: '100%',
                            height: 'fit-content',
                        }}
                        flexDirection={'row'}
                        alignItems={'flex-start'}
                        spacing={3}>
                        <Grid xs={4}>
                            <StyledDetailsBox>
                                <Typography variant={'h4'} ml={1} mt={2}>
                                    Törzsadatok
                                </Typography>
                                <Grid>
                                    <TextField label='Név' variant='standard' fullWidth value={name} onChange={e => setName(e.target.value)} />
                                </Grid>
                                <Grid>
                                    <TextField label='E-mail cím' variant='standard' fullWidth value={email} onChange={e => setEmail(e.target.value)} />
                                </Grid>
                                <Grid>
                                    <TextField label='Telephely' variant='standard' fullWidth value={site} onChange={e => setSite(e.target.value)} />
                                </Grid>
                                <Grid>
                                    <FormControl fullWidth>
                                        <InputLabel id='service-label' variant='standard'>
                                            Szolgáltatási szint
                                        </InputLabel>

                                        <Select
                                            variant='standard'
                                            label='Szolgáltatási szint'
                                            value={serviceLevel}
                                            onChange={e => setServiceLevel(e.target.value as ServiceLevel | '')}>
                                            <MenuItem value={'please_select'}>Kérem válasszon...</MenuItem>
                                            <MenuItem value={ServiceLevel.Basic}>Basic</MenuItem>
                                            <MenuItem value={ServiceLevel.Standard}>Standard</MenuItem>
                                            <MenuItem value={ServiceLevel.Premium}>Premium</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid>
                                    {phoneNumbers.map((phoneNumber, index) => (
                                        <TextField
                                            key={index}
                                            label='Telefonszám'
                                            variant='standard'
                                            fullWidth
                                            value={phoneNumber.Value}
                                            onChange={e => {
                                                setPhoneNumbers(prevPhoneNumbers => {
                                                    const newPhoneNumbers = [...prevPhoneNumbers];
                                                    newPhoneNumbers[index].Value = e.target.value;
                                                    return newPhoneNumbers;
                                                });
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        {phoneNumbers.length === 1 && index === 0 ? (
                                                            <IconButton title='Telefonszám hozzáadása' onClick={handleAddPhoneNumber}>
                                                                <AddCircleOutlineIcon color='primary' />
                                                            </IconButton>
                                                        ) : (
                                                            <>
                                                                {index === phoneNumbers.length - 1 && (
                                                                    <IconButton title='Telefonszám hozzáadása' onClick={handleAddPhoneNumber}>
                                                                        <AddCircleOutlineIcon color='primary' />
                                                                    </IconButton>
                                                                )}

                                                                <IconButton title='Telefonszám törlése' onClick={() => handleDeletePhoneNumber(index)}>
                                                                    <RemoveCircleOutlineIcon color='error' />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    ))}
                                    {!!uuid && (
                                        <CreateOrShowKeycloakUser
                                            uuid={uuid}
                                            keycloakUserId={keycloakUserId}
                                            keycloakUsername={keycloakUsername}
                                            onSuccess={() => fetchGetContractor(uuid)}
                                        />
                                    )}

                                    {isBrAdmin && uuid ? (
                                        <Grid p={0}>
                                            <Button
                                                fullWidth
                                                variant='outlined'
                                                color='error'
                                                size='medium'
                                                startIcon={<Delete />}
                                                onClick={() => setOpenDeleteDialog(true)}>
                                                Alvállalkozó törlése
                                            </Button>

                                            <ConfirmDialog
                                                open={openDeleteDialog}
                                                title='Alvállalkozó törlése'
                                                message='Biztosan szeretné törölni az alvállalkozót?'
                                                onConfirm={handleConfirmDeleteContractor}
                                                onCancel={() => setOpenDeleteDialog(false)}
                                            />
                                        </Grid>
                                    ) : (
                                        <></>
                                    )}
                                </Grid>
                            </StyledDetailsBox>
                        </Grid>

                        <Grid container xs={8} pl={20}>
                            <Typography variant={'h5'} mt={2} sx={{ fontSize: '2rem' }}>
                                Szolgáltatási terület
                            </Typography>

                            <Box sx={{ width: '950px', height: 'auto' }} mb={4}>
                                <ServiceAreaMap onCountyClick={handleShiresClick} selectedShires={selectedShires.map(shire => shire.Name)} />
                            </Box>
                        </Grid>
                    </Grid>
                );

            case ContractorTab.BUSES:
                return (
                    <Grid container>
                        <Grid xs={12}>
                            <StyledDetailsBox>
                                <Typography variant='h4' ml={1} mt={2}>
                                    Buszok
                                </Typography>
                                <Box p={2}>
                                    <BusCards
                                        onAddBusCard={() => {
                                            handleCreateBus(!uuid ? '' : uuid);
                                        }}
                                        busList={busForm}
                                        setBusList={setBusForm}
                                    />
                                </Box>
                            </StyledDetailsBox>
                        </Grid>
                    </Grid>
                );

            case ContractorTab.PRICE_OFFERS:
                return (
                    <Grid container>
                        <StyledDetailsBox >
                            {uuid ? <PriceOffers quotations={quotation} /> : <></>}
                        </StyledDetailsBox>
                    </Grid>
                );

            case ContractorTab.QR_CODES:
                return (
                    <Grid container>
                        <Grid xs={12}>
                            <StyledDetailsBox>
                                <QRCodeTab contractorUuId={uuid} qrCodes={qrCodes} setQrCodes={setQrCodes} />
                            </StyledDetailsBox>
                        </Grid>
                    </Grid>
                );

            default:
                return <></>;
        }
    };

    return (
        <SecuredLayout>
            <Grid container px={3} pt={1}>
                <Grid xs={12}>
                    <Breadcrumbs aria-label='breadcrumb'>
                        <Link component={RouterLink} underline='hover' sx={{ display: 'flex', alignItems: 'center' }} color='inherit' to='/'>
                            <GroupOutlinedIcon sx={{ mr: 0.5 }} fontSize='inherit' />
                            Alvállalkozók
                        </Link>
                        <Typography sx={{ display: 'flex', alignItems: 'center' }} color='inherit'>
                            {!uuid ? (
                                <>
                                    <PersonAddAltOutlinedIcon sx={{ mr: 0.5 }} fontSize='inherit' />
                                    Új alvállalkozó rögzítése
                                </>
                            ) : (
                                <>
                                    <PermIdentityOutlinedIcon sx={{ mr: 0.5 }} fontSize='inherit' />
                                    {name}
                                </>
                            )}
                        </Typography>
                    </Breadcrumbs>
                </Grid>
            </Grid>
            <Paper sx={{ margin: 2, minHeight: 'calc(95vh - 70px)' }}>
                <Grid container px={3} pt={2}>
                    <Grid xs={12}>
                        <Tabs value={selectedTab} onChange={handleTabChange} sx={{ minHeight: '40px', '& .MuiTab-root': { minHeight: '40px', py: 0.5 } }}>
                            <Tab icon={<DescriptionOutlined />} iconPosition='start' label='Törzsadatok' />
                            <Tab icon={<DirectionsBusFilledOutlined />} iconPosition='start' label='Buszok' />
                            <Tab icon={<LocalOfferOutlined />} iconPosition='start' label='Árajánlatok' />
                            <Tab icon={<QrCode2Outlined />} iconPosition='start' label='QR kódok' />
                        </Tabs>
                    </Grid>
                </Grid>
                {renderTabContent()}
                <Grid>
                    {hasChanges() ? (
                        <Tooltip title='Mentés'>
                            <Fab
                                size={'large'}
                                onClick={handleSaveContractor}
                                color='primary'
                                sx={{
                                    borderRadius: 50,
                                    position: 'fixed',
                                    right: '50px',
                                    bottom: '30px',
                                    zIndex: 1000,
                                }}>
                                <SaveIcon />
                            </Fab>
                        </Tooltip>
                    ) : (
                        <></>
                    )}
                </Grid>
                <Grid>
                    <Tooltip title='Vissza a lista nézethez'>
                        <Fab
                            size={'large'}
                            component={RouterLink}
                            to='/'
                            color='info'
                            sx={{
                                borderRadius: 50,
                                position: 'fixed',
                                right: '120px',
                                bottom: '30px',
                                zIndex: 1000,
                            }}>
                            <ArrowBackIcon />
                        </Fab>
                    </Tooltip>
                </Grid>
            </Paper>
        </SecuredLayout>
    );
}
